import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';
import { StringParam, useQueryParam } from 'use-query-params';
import produce from 'immer';
import {
  Experience,
  ExperienceInfoResponse,
  ExperienceStatus,
  Schedule,
} from '@/webapi/use-experience-api';
import { HSpace, VSpace } from '@/components/spacing';
import { Heading1 } from '@/components/typography';
import {
  AudiencePreview,
  ExpStatusChip,
} from '@/features/details/experiment-details-audience-preview';
import { DescriptionTooltip } from '@/components/tooltip';
import { Flexbox } from '@/components/flex';
import { Chip, CONTROL } from '@/features/details/shared';
import { PreviewChip } from '@/features/details/preview-chip';
import { ReBalanceChip } from '@/features/details/rebalance-chip/allocation-chip';
import { DatePicker } from '@/features/details/date-pick';
import { SessionTypeChip } from '@/features/details/sessions-type-chip';
import { AudiencesChip } from '@/features/details/audiences-chip';
import { DownloadOrdersChip } from '@/features/details/download-orders-chip';
import { DotLabel, getChipColor } from '@/components/dot-label';
import {
  getTransformedLabels,
  isRedirectTest,
  parseScheduledTime,
} from '@/utils/experience-utils';
import { formatToLocalDate } from '@/features/details/utils';
import { nav } from '@/utils/browser';
import { editorRoute, Pages } from '@/webapi/pages';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { ExperienceInfoShortLine } from '@/features/shared/experience-info-short-line';
import { LabelsStrip } from '@/features/shared/labels-strip';
import { breakpoints, isMobile } from '@/components/responsive';
import { newMyExperiencesContext } from '@/features/dashboard/experiences/context';
import { ExperienceImagesEnhancedModal } from '@/features/shared/experience-images-enhanced-modal';
import Portal from '@/components/portal';

export function ControlsStrip({
  currentExperienceStatus,
  experience,
  setExperience,
  showPauseModal,
  showArchiveModal,
  pauseRef,
  archiveRef,
}: {
  currentExperienceStatus: ExperienceStatus;
  experience: Experience;
  setExperience: (e: Experience) => void;
  showArchiveModal: () => void;
  showPauseModal: () => void;
  pauseRef: React.MutableRefObject<any>;
  archiveRef: React.MutableRefObject<any>;
}) {
  const {
    id,
    schedule,
    updatedAt: lastEditAt,
    updatedBy: lastEditBy,
    metricsStartAt,
    publishedAt,
    archivedAt,
    endDate,
    versions,
    isPostPurchase,
    status,
    publishedTargeting: targeting,
    isHidden,
    isThemeTest,
    beforeImages,
    afterImages,
    variationImages,
    variants,
  } = experience;
  const { name, description } = experience;
  const isRedirect = isRedirectTest(experience);

  const imagesRef = useRef(null);
  const [isImagesModalOpen, setIsImagesModalOpen] = React.useState(false);

  const onEdit = async () => {
    await nav(editorRoute(experience.id));
  };
  const audienceEnabled = useFeatureBit(
    FeatureBit.SHOW_AUDIENCE_ON_REPORTS_PAGE,
  );

  const { isExpDetailsEnhancement } = newMyExperiencesContext();
  const applyEnhancement = useMemo(
    () => isExpDetailsEnhancement && !(afterImages?.length > 0),
    [isExpDetailsEnhancement, afterImages],
  );

  const onEnd = async () => {
    if (experience.status === ExperienceStatus.PAUSED) {
      showArchiveModal();
    } else if (experience.status === ExperienceStatus.PUBLISHED) {
      showPauseModal();
    }
  };

  const [isPickerVisible, setIsPickerVisible] = useState(false);
  const onDatePickerClicked = () => {
    setIsPickerVisible(!isPickerVisible);
  };
  const [from] = useQueryParam(`from`, StringParam);

  const isMobileDevice = isMobile();

  const bg = getChipColor(currentExperienceStatus);
  if (isHidden) {
    return (
      <AudienceHeader
        name={name}
        description={description}
        id={id}
        isPickerVisible={isPickerVisible}
        onDatePickerClicked={onDatePickerClicked}
      />
    );
  }

  const onExperienceInfoChanged = (info: ExperienceInfoResponse) => {
    setExperience(
      produce(experience, (draft: Experience) => {
        draft.name = info.name;
        draft.description = info.description;
        draft.beforeImages = info.beforeImages;
        draft.afterImages = info.afterImages;
        draft.variationImages = info.variationImages || {};
        draft.labels = info.labels
          ?.filter((l) => l.selected)
          ?.map((l) => l.label);
      }),
    );
  };

  const transformedLabels = getTransformedLabels({
    labels: experience.labels || [],
    isThemeTest,
    isPostPurchase,
    isRedirect,
  });

  const transformedImages = useMemo(() => {
    const allImages = [];
    if (
      !beforeImages &&
      (!variationImages ||
        Object.values(variationImages)
          ?.flatMap((m) => m.values)
          ?.filter((m) => !!m)?.length === 0)
    ) {
      return [];
    }

    allImages.push({
      label: `Control`,
      images: beforeImages?.length > 0 ? beforeImages : [],
      chance: variants?.find((v) => v.name === CONTROL)?.chance || 0,
    });

    const addedVariantIds = new Set();

    if (variationImages && Object.keys(variationImages).length > 0) {
      const existingVariantIds = variants?.map((v) => v.id) || [];

      Object.entries(variationImages)
        .filter(([variantId]) => existingVariantIds.includes(variantId))
        .forEach(([variantId, images]) => {
          const variant = variants?.find((v) => v.id === variantId);
          if (variant) {
            allImages.push({
              label: variant?.name,
              images: images?.values?.length > 0 ? images.values : [],
              chance: variant?.chance || 0,
            });
            addedVariantIds.add(variantId);
          }
        });
    }

    if (variants?.length > 0) {
      variants
        .filter((v) => v.name !== CONTROL && !addedVariantIds.has(v.id))
        .forEach((variant) => {
          allImages.push({
            label: variant?.name,
            images: [],
            chance: variant?.chance || 0,
          });
        });
    }

    const hasAnyImages = allImages.some(
      (item) => item.images && item.images.length > 0,
    );
    return hasAnyImages ? allImages : [];
  }, [beforeImages, variationImages, variants]);

  const backToReports = from === `reports`;
  return (
    <>
      <Portal selector="body">
        {isImagesModalOpen && (
          <ExperienceImagesEnhancedModal
            fromRef={imagesRef}
            onExperienceInfoChanged={onExperienceInfoChanged}
            visible={isImagesModalOpen}
            setVisible={setIsImagesModalOpen}
            currentExperience={experience}
          />
        )}
      </Portal>
      <Controls>
        <BackToLive
          onClick={() =>
            backToReports
              ? nav(`${Pages.DASHBOARD}?pg=REPORTS&scrollTo=uplifts`)
              : nav(Pages.DASHBOARD)
          }
        >{`< Back to ${
          backToReports ? `reports` : `all live experiences`
        } `}</BackToLive>
        <VSpace value={1.5} />
        <Heading1 id="details-heading">
          {name}
          {audienceEnabled && <HSpace className="only-desktop" />}
          {audienceEnabled && (
            <ExpStatusChip
              className="only-desktop"
              bg={bg}
              currentExperienceStatus={currentExperienceStatus}
            />
          )}
        </Heading1>
        <InfoWrapper>
          {transformedLabels?.length > 0 && (
            <>
              <VSpace value={1.5} />
              <LabelsStrip labels={transformedLabels} />
            </>
          )}
          <VSpace className="only-desktop" value={1.8} />
          <ExperienceInfoShortLine
            maxWidth="80rem"
            onExperienceInfoChanged={onExperienceInfoChanged}
            currentExperience={experience}
            nonePlaceholder={
              applyEnhancement ? `+ Add Description` : `+ Add more info`
            }
            isExpDetailsEnhancement={applyEnhancement}
            isControlsStrip
            expandedMode
          />
          <VSpace value={1.5} />
        </InfoWrapper>
        <Flexbox
          direction="row"
          justify="center"
          align="center"
          mbJustify="flex-start"
          mbOverflow="scroll"
          mbPadding="0 2rem"
          className="more-info-box mask"
        >
          {currentExperienceStatus === ExperienceStatus.SCHEDULED ? (
            <ScheduledDateChip
              schedule={schedule}
              status={currentExperienceStatus}
              lastEditAt={lastEditAt}
            />
          ) : (
            <StartDateChip
              metricsStartAt={metricsStartAt}
              status={currentExperienceStatus}
              publishedAt={publishedAt}
              lastEditAt={lastEditAt}
              lastEditBy={lastEditBy}
              endDate={archivedAt || endDate}
            />
          )}
          {audienceEnabled && (
            <AudiencePreview
              trigger={isExpDetailsEnhancement ? experience?.trigger : null}
              targeting={targeting}
            />
          )}
        </Flexbox>
        <VSpace value={2} />
        {!isMobileDevice && transformedImages?.length > 0 && applyEnhancement && (
          <>
            <HighlightImageExp ref={imagesRef}>
              {transformedImages.map((variant, index) => {
                const isVariantImageLive =
                  variant?.chance > 0 &&
                  experience.status === ExperienceStatus.PUBLISHED;
                return (
                  <div
                    className="wrapper"
                    onClick={() => setIsImagesModalOpen(true)}
                    key={index}
                  >
                    {index > 0 && <VsDivider>VS</VsDivider>}

                    <HighlightWrapper live={isVariantImageLive}>
                      <VariantImagesWrapper>
                        <MainImage>
                          {variant.images?.[0] && (
                            <img
                              src={variant.images[0]}
                              alt={`${variant.label} main`}
                              loading="lazy"
                            />
                          )}
                          {!variant.images?.[0] && (
                            <ImageMissingPlaceholder>+</ImageMissingPlaceholder>
                          )}

                          {variant.images.length > 1 && (
                            <Badge>{variant.images.length}</Badge>
                          )}
                        </MainImage>
                        {variant.images.length > 1 &&
                          variant.images
                            .slice(1)
                            .map((image: string, imgIndex: number) => (
                              <ImageWrapper
                                key={`${variant.label}-${imgIndex}`}
                                style={{
                                  zIndex: 5 - imgIndex,
                                  transform: `translate(${
                                    (imgIndex + 1) * 5
                                  }px, ${(imgIndex + 1) * 5}px)`,
                                }}
                              >
                                <img
                                  src={image}
                                  alt={`${variant.label} ${imgIndex + 1}`}
                                  loading="lazy"
                                />
                              </ImageWrapper>
                            ))}
                      </VariantImagesWrapper>
                      <p>
                        <span />
                        {variant.label || ``}
                      </p>
                    </HighlightWrapper>
                  </div>
                );
              })}
            </HighlightImageExp>
            <VSpace value={4} />
          </>
        )}
        <ControlsLayout ref={pauseRef} className="mask">
          {!isMobileDevice && !archivedAt && (
            <Chip whiteBg={isExpDetailsEnhancement} onClick={onEdit}>
              <EditIcon />
              Edit
            </Chip>
          )}
          {!archivedAt && (
            <Chip
              whiteBg={isExpDetailsEnhancement}
              ref={archiveRef}
              onClick={onEnd}
            >
              <EndIcon />
              {status === ExperienceStatus.PAUSED ? `Archive` : `Pause`}
            </Chip>
          )}
          {!isPostPurchase && (
            <PreviewChip
              whiteBg={isExpDetailsEnhancement}
              experience={experience}
            />
          )}
          <ReBalanceChip
            whiteBg={isExpDetailsEnhancement}
            experience={experience}
            setExperience={setExperience}
          />
          <DatePicker
            whiteBg={isExpDetailsEnhancement}
            open={isPickerVisible}
            onClick={onDatePickerClicked}
          />
          {!isPostPurchase && (
            <SessionTypeChip
              whiteBg={isExpDetailsEnhancement}
              expId={id}
              metricsStart={metricsStartAt}
            />
          )}
          {!isPostPurchase && (
            <AudiencesChip
              whiteBg={isExpDetailsEnhancement}
              experience={experience}
            />
          )}
          {!isPostPurchase && !isMobileDevice && (
            <DownloadOrdersChip
              expId={id}
              metricsStart={metricsStartAt}
              versions={versions}
              experience={experience}
              whiteBg={isExpDetailsEnhancement}
            />
          )}
        </ControlsLayout>
        <VSpace value={2.5} />
      </Controls>
    </>
  );
}

const DescrWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  :hover {
    color: #4f5359;
  }

  :active {
    color: #89959c;
  }
`;
const Descr = styled.p`
  margin-top: 2rem;
  color: black;
  font-size: 1.2rem;
  font-family: Inter, serif;
  font-weight: 400;

  width: 600px;
  text-overflow: ellipsis;
  overflow-x: hidden;
  white-space: nowrap;
`;
const BackToLive = styled.a`
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 500;
  color: #89959c;
  font-family: Inter, serif;
  cursor: pointer;
  transition: color 0.3s linear;

  :hover {
    color: #4f5359;
  }

  :active {
    color: #89959c;
  }
`;

function EditIcon() {
  return (
    <>
      <StaticImage
        src="../../assets/edit.svg"
        alt="edit"
        placeholder="none"
        loading="eager"
        height={10}
      />
      <HSpace value={0.5} />
    </>
  );
}

function EndIcon() {
  return (
    <>
      <StaticImage
        src="../../assets/stop.svg"
        alt="end"
        placeholder="none"
        loading="eager"
        height={10}
      />
      <HSpace value={0.5} />
    </>
  );
}

const Controls = styled.div`
  margin: 14px auto 0 auto;
  text-align: center;
  max-width: 1280px;

  #details-heading {
    text-align: center;
    max-width: 90%;
    margin: 0 auto;
  }

  ${breakpoints.down(`md`)} {
    max-width: 100vw;

    .more-info-box > * {
      flex: 0 0 auto;
    }

    .mask {
      mask-image: linear-gradient(
        var(--mask-direction, to right),
        hsl(0 0% 0% / 0),
        rgb(248 249 251 / 1) 10%,
        rgb(248 249 251 / 1) 90%,
        hsl(0 0% 0% / 0)
      );
    }
  }
`;

const ControlsLayout = styled(Flexbox)`
  && {
    position: relative;
    flex-direction: row;
    justify-content: center;

    gap: 1rem;

    ${breakpoints.down(`md`)} {
      justify-content: flex-start;
      overflow-x: scroll;
      padding: 0 2rem;

      && > * {
        flex: 0 0 auto;
      }
    }
  }
`;

const HighlightImageExp = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-wrap: nowrap;
  overflow: scroll;
  padding: 10px;
  margin: -10px;

  && > div.wrapper {
    display: flex;
    flex-direction: row;
    cursor: pointer;
  }
`;

const HighlightWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1rem;

  && > p {
    font-family: 'Inter', sans-serif;
    font-size: 1.3rem;
    color: #434d56;
    margin: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    transform: translateX(-10px);
  }

  && > p > span {
    min-width: 10px;
    min-height: 10px;
    max-height: 10px;
    border-radius: 100%;
    margin-top: 2px;
    background-color: ${({ live }) => (live > 0 ? `#2EDA18` : `#798794`)};
  }
  }
`;
const MainImage = styled.div`
  border-radius: 20px;
  width: auto;
  height: 100%;
  z-index: 6;
  position: relative;

  && > img {
    border-radius: 20px;
    width: 100%;
    height: 100%;
    box-shadow: rgba(0, 0, 0, 0.2) 3px 3px 5px;
  }
`;

const ImageMissingPlaceholder = styled.div`
  height: 100%;
  width: 100%;
  max-height: 100%;
  max-width: 100%;
  display: block;
  border-radius: 20px;
  box-shadow: rgba(0, 0, 0, 0.2) 3px 3px 5px;
  background-color: #fff;
  padding: 4.4rem 10rem 5rem 10rem;
  color: rgb(166, 185, 194);
  font-size: 8rem;
  align-self: center;
`;

const VariantImagesWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 300px;
  max-width: 350px;
  margin: 13px 0 25px 0;
`;

const ImageWrapper = styled.div`
  position: absolute;
  border-radius: 20px;
  overflow: hidden;
  height: 100%;
  width: 100%;

  img {
    max-height: 100%;
    max-width: 100%;
    margin: auto;
    object-fit: contain;
    display: block;
    border-radius: 20px;
    box-shadow: rgba(0, 0, 0, 0.2) 3px 3px 5px;
  }
`;

const VsDivider = styled.span`
  font-size: 1.44rem;
  color: #a6afb8;
  letter-spacing: -0.1em;
  align-self: center;
  margin-bottom: 44px;
  margin-left: 3rem;
  margin-right: 3rem;
`;

const Badge = styled.div`
  position: absolute;
  width: 25px;
  height: 25px;
  border-radius: 100%;
  background-color: #e8e8e9;
  font-family: 'Inter', sans-serif;
  text-align: center;
  line-height: 25px;
  font-size: 1.1rem;
  top: -10px;
  right: -10px;
  z-index: 10;
  box-shadow: rgba(0, 0, 0, 0.15) 0 2px 5px;
`;

function StartDateChip({
  metricsStartAt,
  publishedAt,
  lastEditAt,
  status,
  endDate,
  lastEditBy,
}: {
  metricsStartAt: string | undefined;
  publishedAt: string | undefined;
  lastEditAt: string | undefined;
  status: ExperienceStatus;
  endDate: string | Date | undefined;
  lastEditBy: string | undefined;
}) {
  let lastEdit = ` (Last edit ${formatToLocalDate(lastEditAt)}${
    lastEditBy ? ` by ${lastEditBy?.replace(`loomi.me`, `visually.io`)}` : ``
  })`;
  if (status === ExperienceStatus.ARCHIVED && endDate) {
    lastEdit = ` (End date ${formatToLocalDate(endDate)})`;
  }

  return (
    <DotLabel dotColor={getChipColor(status)} textColor="#6b6b6b" noDot>
      <StartDateText>
        {`Start date: ${formatToLocalDate(metricsStartAt || publishedAt)}${
          lastEditAt ? lastEdit : ``
        }`}
      </StartDateText>
    </DotLabel>
  );
}

function ScheduledDateChip({
  schedule,
  status,
  lastEditAt,
}: {
  schedule: Schedule | undefined;
  lastEditAt: string | undefined;
  status: ExperienceStatus;
}) {
  return (
    <DotLabel noDot dotColor={getChipColor(status)} textColor="#6b6b6b">
      <StartDateText>
        {`Scheduled ${parseScheduledTime(schedule)} ${
          lastEditAt ? ` (Last edit  ${formatToLocalDate(lastEditAt)})` : ``
        }`}
      </StartDateText>
    </DotLabel>
  );
}

const InfoWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 60%;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  flex-direction: column;

  .exp-short-line-wrapper {
    color: black;
  }

  .labels-strip-wrapper .labels-strip-chip {
    border: 1px solid rgba(201, 208, 217, 0.65);
  }
  .labels-strip-wrapper span {
    color: #7b8287;
  }

  ${breakpoints.down(`md`)} {
    max-width: 90%;
    .exp-short-line-wrapper {
      display: none;
    }
  }
`;

const StartDateText = styled.span`
  margin: 1rem 0;
  color: black;
  font-size: 1.2rem;
  font-family: Inter, serif;
  font-weight: 400;
`;
function AudienceHeader({
  description,
  id,
  name,
  onDatePickerClicked,
  isPickerVisible,
}: {
  name: string;
  description: string;
  id: string;
  isPickerVisible: boolean;
  onDatePickerClicked: () => void;
}) {
  const [audience] = useQueryParam(`aud`);
  switch (audience) {
    case `m`:
      name = `Mobile Visitors`;
      description = `Mobile visitors only`;
      break;
    case `d`:
      name = `Desktop Visitors`;
      description = `Desktop visitors only`;
      break;
    case `r`:
      name = `Returning Visitors`;
      description = `Returning visitors only`;
      break;
    case `n`:
      name = `New Visitors`;
      description = `New visitors only`;
      break;
    default:
  }

  return (
    <Controls>
      <BackToLive
        onClick={() => nav(`${Pages.DASHBOARD}?pg=AUDIENCES`)}
      >{`< Back to all audiences`}</BackToLive>
      <VSpace value={1.5} />
      <Heading1 id="details-heading">
        {name}
        <VSpace />
      </Heading1>
      {description ? (
        <DescrWrapper>
          <DescriptionTooltip id={id} description={description} />
          <Descr data-tip data-for={`desc${id}`}>
            {description || ``}
          </Descr>
        </DescrWrapper>
      ) : (
        <VSpace value={0.85} />
      )}
      <VSpace />
      <div style={{ width: `400px`, margin: `0 auto` }}>
        <DatePicker open={isPickerVisible} onClick={onDatePickerClicked} />
      </div>
      <VSpace value={3} />
    </Controls>
  );
}
